/**
 * Casca do Portal Operacional de Frota e Manutenção.
 *
 * Não é o `AppShell` reduzido: aquele tem sidebar fixa de 256 px e não cabe num
 * celular. Aqui a mesma identidade (cabeçalho `tm-900`, verde Transmasi, cards,
 * botões e campos do SIAG) organizada para o polegar:
 * - celular: cabeçalho enxuto + navegação inferior fixa com cinco destinos;
 * - tablet/desktop (`md+`): os mesmos destinos em abas, conteúdo centralizado.
 */
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import {
  IconChevronLeft, IconClipboard, IconHome, IconShield, IconSparkle, IconTruck, IconWrench,
} from "../../components/icons";
import { PORTAL_NAV, activeNavKey, type PortalNavItem } from "./portal-nav";
import { SIAG_HOME } from "./open-app";
import { usePortalManifest } from "./usePortalManifest";

const ICONE: Record<PortalNavItem["key"], React.ComponentType<{ className?: string }>> = {
  inicio: IconHome,
  checklists: IconClipboard,
  ocorrencias: IconShield,
  veiculos: IconTruck,
  manutencao: IconWrench,
};

export function PortalShell() {
  usePortalManifest();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const ativo = activeNavKey(pathname);

  return (
    <div className="min-h-screen bg-surface-muted">
      <header className="sticky top-0 z-30 bg-tm-900 text-white pt-[env(safe-area-inset-top)]">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-tm-500 grid place-items-center flex-shrink-0">
            <IconSparkle className="w-4 h-4 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold leading-tight truncate">Frota e Manutenção</div>
            <div className="text-xs text-tm-300 leading-tight truncate">{user?.name ?? "Portal operacional"}</div>
          </div>
          <a
            href={SIAG_HOME}
            className="inline-flex items-center gap-1 min-h-[44px] px-2 -mr-2 rounded-lg text-sm text-tm-100/80 hover:text-white hover:bg-white/5"
          >
            <IconChevronLeft className="w-4 h-4" />
            <span>Voltar ao SIAG</span>
          </a>
        </div>
      </header>

      {/* Abas — tablet e desktop */}
      <nav className="hidden md:block bg-white border-b border-surface-border sticky top-14 z-20" aria-label="Portal">
        <div className="max-w-3xl mx-auto px-4 flex gap-1">
          {PORTAL_NAV.map((item) => {
            const Icone = ICONE[item.key];
            const on = ativo === item.key;
            return (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.end}
                className={`flex items-center gap-2 px-3 py-3 text-sm border-b-2 -mb-px transition-colors ${
                  on ? "border-tm-500 text-tm-700 font-medium" : "border-transparent text-gray-500 hover:text-gray-900"
                }`}
              >
                <Icone className="w-4 h-4" />
                {item.label}
              </NavLink>
            );
          })}
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 pt-4 pb-28 md:pb-10">
        <Outlet />
      </main>

      {/* Navegação inferior — celular */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-surface-border pb-[env(safe-area-inset-bottom)]"
        aria-label="Portal"
      >
        <div className="grid grid-cols-5">
          {PORTAL_NAV.map((item) => {
            const Icone = ICONE[item.key];
            const on = ativo === item.key;
            return (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.end}
                className={`flex flex-col items-center justify-center gap-0.5 min-h-[56px] text-[11px] ${
                  on ? "text-tm-600 font-medium" : "text-gray-500"
                }`}
              >
                <Icone className={`w-5 h-5 ${on ? "text-tm-500" : "text-gray-400"}`} />
                {item.label}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
